import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { useParams } from "react-router-dom";
import AllHoldingsModal from "../AllHoldingsModal";
import { useInvestmentOrder } from "features/Explore/hook";
import {
  Card,
  CelebrationCard,
  TabsContainer,
  Tab,
  FormContent,
  FormGroupRow,
  FormLabel,
  AvailableCashRow,
  AvailableCashLabel,
  AvailableCashAmount,
  Divider,
  AlertBox,
  AlertIcon,
  AlertText,
  Button,
  BackButton,
  BackIcon,
  SummaryTitle,
  SummarySection,
  SummaryRow,
  SummaryLabel,
  SummaryValue,
  SubmitButton,
  Spinner,
  OrderSubmittedHeader,
  OrderSubmittedTitle,
  OrderSubmittedTime,
  ViewDetailsButton,
  ControlContainer,
  StrategyLogo,
  StrategyInfo,
  ButtonsContainer,
  LockIcon,
  InputHelperRow,
  SellAllButton,
} from "./InvestmentOrderCard.styles";

import Dropdown from "react-bootstrap/Dropdown";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import "bootstrap/dist/css/bootstrap.min.css";
import { iconDropdownArrow } from "assets/icons/index";
import { isFreeUser } from "utils";
import { userDetailSelect } from "Layout/slice/selector";
import { ChoosePlanModal } from "features/Account/v2/ChoosePlanModal/index";
import confetti from "canvas-confetti";
import { BLOCKED_BROKERS } from "utils/constants";

const InvestmentOrderCard = ({ isPublicStrategy = false }) => {
  const { id: strategyId } = useParams();
  const [showPurchasePlanModal, setShowPurchasePlanModal] = useState(false);
  const togglePurchasePlanModal = useCallback(() => {
    setShowPurchasePlanModal((prev) => !prev);
  }, []);

  const cardRef = useRef(null);
  const user = useSelector(userDetailSelect);
  // Lock should only apply to plan restrictions (free). Users on Free plan without a confirmed internal account are locked.
  const isPlanLocked = useMemo(
    () => isFreeUser() && !user?.is_surmount_internal_created,
    [user?.is_surmount_internal_created]
  );
  const isPublicDisabled = Boolean(isPublicStrategy);

  const {
    // State
    activeTab,
    step,
    selectedAccount,
    investmentAmount,
    isSubmitting,
    orderSubmitted,
    isOpen,
    strategyData,
    tradingConnectionData,
    isLoadingCash,
    isHoldingsModalOpen,
    showSellAll,

    // API Data
    portfolios,
    loading,
    isError,
    holdingsData,
    isLoadingHoldings,
    isHoldingsError,

    // Computed values
    availableCashFormatted,
    strategy,
    additionalCash,
    showAdditionalCashAlert,
    isAmountValid,

    // Setters
    setActiveTab,
    setInvestmentAmount,
    setIsOpen,
    setIsHoldingsModalOpen,

    // Event handlers
    handleNext,
    handleBack,
    handleSubmitOrder,
    handleDone,
    handleViewDetails,
    handleAccountSelect,
    handleSellAll,
  } = useInvestmentOrder(strategyId);

  useEffect(() => {
    if (!orderSubmitted || !cardRef.current) return;

    const canvas = document.createElement("canvas");
    canvas.style.position = "absolute";
    canvas.style.top = 0;
    canvas.style.left = 0;
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.pointerEvents = "none";
    cardRef.current.appendChild(canvas);

    const myConfetti = confetti.create(canvas, { resize: true });
    const count = 200;
    const defaults = { origin: { y: 0.7 } };

    function fire(particleRatio, opts) {
      myConfetti({
        ...defaults,
        ...opts,
        particleCount: Math.floor(count * particleRatio),
      });
    }

    // Fire sequence
    fire(0.25, { spread: 26, startVelocity: 55 });
    fire(0.2, { spread: 60 });
    fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
    fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
    fire(0.1, { spread: 120, startVelocity: 45 });

    return () => {
      myConfetti.reset();
      if (canvas.parentNode) {
        canvas.parentNode.removeChild(canvas);
      }
    };
  }, [orderSubmitted]);

  // Memoized display value for Available cash / Current Allocation
  const allocationOrCashDisplay = useMemo(() => {
    if (activeTab === "sell") {
      const match = tradingConnectionData?.trading_connections?.find(
        (tc) => String(tc?.strategy_id) === String(strategyId)
      );
      return match?.current_allocation?.dollars?.formatted || "Not available";
    }
    return availableCashFormatted;
  }, [activeTab, tradingConnectionData, strategyId, availableCashFormatted]);

  const filteredPortfolios = useMemo(() => {
    if (!portfolios) return [];
    return portfolios.filter((account) => {
      const identifier = account.broker_key || account.broker_name || "";
      return !BLOCKED_BROKERS.includes(identifier.toUpperCase());
    });
  }, [portfolios]);

  // Order Submitted View
  if (orderSubmitted) {
    return (
      <>
        <CelebrationCard ref={cardRef}>
          <OrderSubmittedHeader>
            <OrderSubmittedTitle>
              {activeTab === "sell"
                ? "Sell Order Submitted"
                : "Buy Order Submitted"}
            </OrderSubmittedTitle>
            <OrderSubmittedTime>
              {new Date().toLocaleDateString("en-US", {
                weekday: "short",
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              })}
            </OrderSubmittedTime>
          </OrderSubmittedHeader>

          <SummarySection>
            <SummaryRow>
              <SummaryLabel>Strategy</SummaryLabel>
              <SummaryValue>
                <StrategyInfo>
                  {strategyData?.logo_url && (
                    <StrategyLogo
                      src={strategyData.logo_url}
                      alt={strategy}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  )}
                  <span>{strategy}</span>
                </StrategyInfo>
              </SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>Account</SummaryLabel>
              <SummaryValue>
                <StrategyInfo>
                  {selectedAccount?.logo_url && (
                    <StrategyLogo
                      src={selectedAccount.logo_url}
                      alt={selectedAccount?.broker_name || "Broker"}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  )}
                  <span>
                    {selectedAccount?.broker_name ||
                      selectedAccount?.broker_name ||
                      "Selected account"}
                  </span>
                </StrategyInfo>
              </SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>Estimated cost</SummaryLabel>
              <SummaryValue $amount>${investmentAmount} USD</SummaryValue>
            </SummaryRow>
          </SummarySection>

          <ButtonsContainer>
            <Button onClick={handleDone}>Done</Button>
            <ViewDetailsButton onClick={handleViewDetails}>
              View details
            </ViewDetailsButton>
          </ButtonsContainer>
        </CelebrationCard>

        <AllHoldingsModal
          isOpen={isHoldingsModalOpen}
          onClose={() => {
            setIsHoldingsModalOpen(false);
          }}
          holdings={isLoadingHoldings ? [] : holdingsData?.data || []}
          strategyName={strategy}
          isLoading={isLoadingHoldings}
          error={isHoldingsError}
        />
        {/* Purchase Plan Modal for locked interactions */}
        {/* <PurchasePlanModal
          show={showPurchasePlanModal}
          onHide={togglePurchasePlanModal}
          isCancelPlan={false}
          isChangePlan={true}
        /> */}
        <ChoosePlanModal
          show={showPurchasePlanModal}
          onHide={togglePurchasePlanModal}
        />
      </>
    );
  }

  // Summary View (Step 2)
  if (step === 2) {
    return (
      <>
        <Card>
          <BackButton onClick={handleBack}>
            <BackIcon>←</BackIcon> Back
          </BackButton>

          <SummaryTitle>Summary</SummaryTitle>

          <SummarySection>
            <SummaryRow>
              <SummaryLabel>Strategy</SummaryLabel>
              <SummaryValue>
                <StrategyInfo>
                  {strategyData?.logo_url && (
                    <StrategyLogo
                      src={strategyData.logo_url}
                      alt={strategy}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  )}
                  <span>{strategy}</span>
                </StrategyInfo>
              </SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>Account</SummaryLabel>
              <SummaryValue>
                {selectedAccount?.broker_name ||
                  selectedAccount?.broker_name ||
                  "Selected account"}
              </SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>Investment amount</SummaryLabel>
              <SummaryValue $amount>${investmentAmount} USD</SummaryValue>
            </SummaryRow>
          </SummarySection>

          <SubmitButton
            onClick={handleSubmitOrder}
            disabled={isSubmitting}
            $submitting={isSubmitting}
          >
            Submit order {isSubmitting && <Spinner />}
          </SubmitButton>
        </Card>

        <AllHoldingsModal
          isOpen={isHoldingsModalOpen}
          onClose={() => {
            setIsHoldingsModalOpen(false);
          }}
          holdings={isLoadingHoldings ? [] : holdingsData?.data || []}
          strategyName={strategy}
          isLoading={isLoadingHoldings}
          error={isHoldingsError}
        />
        {/* Purchase Plan Modal for locked interactions */}
        {/* <PurchasePlanModal
          show={showPurchasePlanModal}
          onHide={togglePurchasePlanModal}
          isCancelPlan={false}
          isChangePlan={true}
        /> */}
        <ChoosePlanModal
          show={showPurchasePlanModal}
          onHide={togglePurchasePlanModal}
        />
      </>
    );
  }

  // Initial Form View (Step 1)
  return (
    <>
      <Card>
        <TabsContainer>
          <Tab
            $active={activeTab === "buy"}
            onClick={() => setActiveTab("buy")}
          >
            Buy
          </Tab>
          <Tab
            $active={activeTab === "sell"}
            onClick={() => setActiveTab("sell")}
          >
            Sell
          </Tab>
        </TabsContainer>

        <FormContent>
          {/* Account Selection */}
          <FormGroupRow style={{ marginBottom: "1rem" }}>
            <FormLabel>Account</FormLabel>
            <ControlContainer>
              <Dropdown
                onToggle={(open) => setIsOpen(open)}
                onSelect={handleAccountSelect}
                className="custom-dropdown"
              >
                <Dropdown.Toggle
                  variant="light"
                  id="account-dropdown"
                  className=""
                  disabled={isPublicStrategy}
                >
                  {selectedAccount ? (
                    <div className="brokder-info-wrapper">
                      <img
                        src={selectedAccount.logo_url}
                        alt={selectedAccount.broker_name}
                        width={20}
                        height={20}
                      />
                      <span className="broker-name">
                        {selectedAccount.broker_name ||
                          selectedAccount.broker_name}
                      </span>
                    </div>
                  ) : (
                    "Select an account"
                  )}
                  <img
                    src={iconDropdownArrow}
                    className={`dropdown-arrow ${isOpen ? "rotated" : ""}`}
                  />
                </Dropdown.Toggle>

                <Dropdown.Menu className="custom-dropdown-menu">
                  {loading ? (
                    <Dropdown.Item disabled>
                      <div className="account-info">
                        <div className="account-details">
                          <div className="account-name">
                            Loading accounts...
                          </div>
                        </div>
                      </div>
                    </Dropdown.Item>
                  ) : isError ? (
                    <Dropdown.Item disabled>
                      <div className="account-info">
                        <div className="account-details">
                          <div className="account-name">
                            Failed to load broker accounts
                          </div>
                        </div>
                      </div>
                    </Dropdown.Item>
                  ) : filteredPortfolios?.length === 0 ? (
                    <Dropdown.Item disabled>
                      <div className="account-info">
                        <div className="account-details">
                          <div className="account-name">
                            No accounts available
                          </div>
                        </div>
                      </div>
                    </Dropdown.Item>
                  ) : (
                    filteredPortfolios?.map((account) => {
                      const isNotConnected =
                        account.subscription_status?.toLowerCase()?.trim() !==
                        "active";
                      const isDisabled = activeTab === "sell" && isNotConnected;
                      return (
                        <Dropdown.Item
                          key={account.broker_account_id}
                          eventKey={account.broker_account_id}
                          disabled={isDisabled}
                          active={
                            selectedAccount?.broker_account_id ===
                            account.broker_account_id
                          }
                          className={`custom-dropdown-item ${
                            selectedAccount?.broker_account_id ===
                            account.broker_account_id
                              ? "active-item"
                              : ""
                          } ${isDisabled ? "disabled-item" : ""}`}
                          title={`${
                            account.broker_account_nickname ||
                            account.broker_name
                          } (${account.subscription_status})`}
                        >
                          <div className="account-info">
                            <img
                              src={account.logo_url}
                              alt={`${account.broker_name} logo`}
                            />

                            <div className="account-details">
                              <div className="account-name">
                                {account.broker_account_nickname ||
                                  account.broker_name}
                              </div>
                              <div className="account-balance">
                                {account.subscription_status}
                              </div>
                            </div>
                          </div>

                          <div
                            className={`radio-indicator ${
                              selectedAccount?.broker_account_id ===
                              account.broker_account_id
                                ? "selected"
                                : ""
                            }`}
                          />
                        </Dropdown.Item>
                      );
                    })
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </ControlContainer>
          </FormGroupRow>

          {selectedAccount && (
            <AvailableCashRow>
              <AvailableCashLabel>
                {activeTab === "sell" ? "Current Allocation" : "Available cash"}
              </AvailableCashLabel>
              <AvailableCashAmount>
                {isLoadingCash ? (
                  <Spinner />
                ) : tradingConnectionData ? (
                  allocationOrCashDisplay
                ) : (
                  "Unable to load"
                )}
              </AvailableCashAmount>
            </AvailableCashRow>
          )}

          {/* Investment Amount */}
          <FormGroupRow style={{ marginTop: "1.5rem" }}>
            <FormLabel>Investment amount</FormLabel>
            <ControlContainer>
              <InputGroup>
                <Form.Control
                  type="number"
                  value={investmentAmount}
                  onChange={(e) => setInvestmentAmount(e.target.value)}
                  placeholder="0.00"
                  min="0"
                  disabled={isPublicStrategy}
                />
                <InputGroup.Text>USD</InputGroup.Text>
              </InputGroup>
              {showSellAll && !isPublicStrategy && (
                <InputHelperRow>
                  <SellAllButton onClick={handleSellAll} type="button">
                    Sell All
                  </SellAllButton>
                </InputHelperRow>
              )}
            </ControlContainer>
          </FormGroupRow>
          {!isAmountValid && investmentAmount !== "" && (
            <AlertBox>
              <AlertIcon>!</AlertIcon>
              <AlertText>Enter a valid amount greater than 0.</AlertText>
            </AlertBox>
          )}

          {showAdditionalCashAlert && (
            <>
              <Divider />
              <AlertBox>
                <AlertText>
                  You will add <strong>${additionalCash} USD</strong> in the
                  next step to complete this order.
                </AlertText>
              </AlertBox>
            </>
          )}
          <Button
            title={
              isPublicDisabled
                ? "Public strategies cannot be ordered from here."
                : isPlanLocked
                ? "Upgrade your plan to proceed to the next step."
                : "Proceed to the order summary."
            }
            onClick={
              isPublicDisabled
                ? undefined
                : isPlanLocked
                ? togglePurchasePlanModal
                : handleNext
            }
            disabled={
              isPublicDisabled ||
              (!isPlanLocked && (!selectedAccount || !isAmountValid))
            }
          >
            {isPlanLocked && !isPublicDisabled && <LockIcon />}
            Next
          </Button>
        </FormContent>
      </Card>

      <AllHoldingsModal
        isOpen={isHoldingsModalOpen}
        onClose={() => {
          setIsHoldingsModalOpen(false);
        }}
        holdings={isLoadingHoldings ? [] : holdingsData?.data || []}
        strategyName={strategy}
        isLoading={isLoadingHoldings}
        error={isHoldingsError}
      />
      {/* Purchase Plan Modal for locked interactions */}
      {/* <PurchasePlanModal
        show={showPurchasePlanModal}
        onHide={togglePurchasePlanModal}
        isCancelPlan={false}
        isChangePlan={true}
      /> */}
      <ChoosePlanModal
        show={showPurchasePlanModal}
        onHide={togglePurchasePlanModal}
      />
    </>
  );
};

InvestmentOrderCard.propTypes = {
  isPublicStrategy: PropTypes.bool,
};

export default InvestmentOrderCard;
